import Debug from 'debug';
import { getUid } from 'ol/util';
import Feature from 'ol/Feature';
import {scale as scaleCoordinate, add as addCoordinate} from 'ol/coordinate';
import { buffer, extend, createEmpty, createOrUpdateFromCoordinate } from 'ol/extent';
import Point from 'ol/geom/Point';
import Cluster from 'ol/source/Cluster';

const debug = Debug('amms:modules:openlayers:sources:customCluster');
const rtDebug = Debug('rt:amms:modules:openlayers:sources:customCluster');

/**
 * @typedef {Object} Options
 * @property {import("./Source.js").AttributionLike} [attributions] Attributions.
 * @property {number} [distance=20] Minimum distance in pixels between clusters.
 * @property {function(Feature):Point} [geometryFunction]
 * Function that takes an {@link module:ol/Feature} as argument and returns an
 * {@link module:ol/geom/Point} as cluster calculation point for the feature. When a
 * feature should not be considered for clustering, the function should return
 * `null`. The default, which works when the underyling source contains point
 * features only, is
 * ```js
 * function(feature) {
 *   return feature.getGeometry();
 * }
 * ```
 * See {@link module:ol/geom/Polygon~Polygon#getInteriorPoint} for a way to get a cluster
 * calculation point for polygons.
 * @property {VectorSource} source Source.
 * @property {boolean} [wrapX=true] Whether to wrap the world horizontally.
 */


/**
 * @classdesc
 * Layer source to cluster vector data. Works out of the box with point
 * geometries. For other geometry types, or if not all geometries should be
 * considered for clustering, a custom `geometryFunction` can be defined.
 * @api
 */
class CustomCluster extends Cluster {
    constructor(options) {
        super(options);

        debug('Creating custom cluster');

        this.filter = options.filter;

        this.beforeCluster = options.beforeCluster;

        this.clusterFunction = options.clusterFunction || function(features) { return features };

        this.createClusterFunction = options.createClusterFunction || function(point, features) {
            const cluster = new Feature(point);
            cluster.set('features', features);
            return [cluster];
        };
    }

    /**
     * @protected
     */
    cluster() {
        if (this.resolution === undefined) {
            return;
        }

        rtDebug('Clustering');

        this.features.length = 0;
        const mapDistance = this.distance * this.resolution;
        const features = this.source.getFeatures();

        if(this.beforeCluster) {
            this.beforeCluster();
        }

        let featuresToCluster;
        if(this.filter) {
            featuresToCluster = [];
            for(let i = 0, length = features.length; i < length; i += 1) {
                const feature = features[i];

                if(this.filter(feature)) {
                    featuresToCluster.push(feature);
                }
                else {
                    this.features.push(feature);
                }
            }
        }
        else {
            featuresToCluster = features;
        }


        /**
         * @type {!Object<string, boolean>}
         */
        const clustered = {};

        // for (let i = 0, ii = featuresToCluster.length; i < ii; i++) {
        //     const feature = featuresToCluster[i];
        //     if (!(getUid(feature) in clustered)) {
        //         const geometry = this.geometryFunction(feature);
        //         if (geometry) {
        //             const coordinates = geometry.getCoordinates();
        //             createOrUpdateFromCoordinate(coordinates, extent);
        //             buffer(extent, mapDistance, extent);
        //
        //             let neighbors = this.source.getFeaturesInExtent(extent);
        //             neighbors = neighbors.filter((neighbor) => {
        //                 if(this.filter && !this.filter(neighbor)) {
        //                     return false;
        //                 }
        //
        //                 const uid = getUid(neighbor);
        //                 if (!(uid in clustered)) {
        //                     clustered[uid] = true;
        //                     return true;
        //                 } else {
        //                     return false;
        //                 }
        //             });
        //
        //             this.features.push(
        //                 ...( this.clusterFunction(this.createCluster(neighbors)) )
        //             );
        //         }
        //     }
        // }


        for (let i = 0, ii = featuresToCluster.length; i < ii; i++) {
            const extent = createEmpty();
            const feature = featuresToCluster[i];
            if (!(getUid(feature) in clustered)) {
                const geometry = this.geometryFunction(feature);
                if (geometry) {
                    const coordinates = geometry.getCoordinates();
                    // createOrUpdateFromCoordinate(coordinates, extent);
                    extend(extent, createOrUpdateFromCoordinate(coordinates));
                    buffer(extent, mapDistance, extent);

                    const clusterFeatures = [];
                    let neighbors;
                    do {
                        neighbors = this.source.getFeaturesInExtent(extent);
                        neighbors = neighbors.filter((neighbor) => {
                            if(this.filter && !this.filter(neighbor)) {
                                return false;
                            }

                            const uid = getUid(neighbor);
                            if (!(uid in clustered)) {
                                clustered[uid] = true;

                                extend(extent, createOrUpdateFromCoordinate(neighbor.getGeometry().getCoordinates()));

                                return true;
                            } else {
                                return false;
                            }
                        });

                        clusterFeatures.push(...neighbors);
                    }
                    while(neighbors.length);


                    extent = createEmpty();

                    this.features.push(
                        ...( this.clusterFunction(this.createCluster(clusterFeatures)) )
                    );
                }
            }
        }
    }

    /**
     * @param {Array<Feature>} features Features
     * @return {Feature} The cluster feature.
     * @protected
     */
    createCluster(features) {
        rtDebug('Creating cluster');

        const centroid = [0, 0];
        for (let i = features.length - 1; i >= 0; --i) {
            const geometry = this.geometryFunction(features[i]);
            if (geometry) {
                addCoordinate(centroid, geometry.getCoordinates());
            } else {
                features.splice(i, 1);
            }
        }
        scaleCoordinate(centroid, 1 / features.length);

        return this.createClusterFunction(new Point(centroid), features);
    }
}

export default CustomCluster;